import { cn } from "../../lib/utils";
import React, { useRef, useState } from "react";
import { useOnClickOutside } from "usehooks-ts";

interface MultiselectProps {
    options: Object;
    selected: string[];
    onChange: (selected: string[]) => void;
    placeholder: string;
    className?: string;
}

export default function Multiselect({ options, selected, onChange, placeholder, className }: MultiselectProps) {
    const [isOpen, setIsOpen] = useState(false);
    const ref = useRef(null);
    const length = Object.keys(selected).length;

    function isSelected(key: string): boolean {
        return selected.includes(key);
    }

    function toggleOption(key: string): void {
        let newSelected = [...selected];
        if (isSelected(key)) {
            newSelected = newSelected.filter((k) => k !== key);
        } else {
            newSelected.push(key);
        }
        onChange(newSelected);
    }

    function getValue(key: string): string | undefined {
        const keys = Object.keys(options);
        const index = keys.findIndex((k) => k === key);
        if (index !== -1) {
            const values = Object.values(options);
            return values[index];
        }
        return undefined;
    }

    useOnClickOutside(ref, () => setIsOpen(false));

    return (
        <div ref={ref}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className={cn(
                    "flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-1 text-sm  transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
                    className
                )}>
                {length > 0 ? (
                    <div className="flex w-full items-center gap-1 overflow-x-hidden">
                        {selected.map((s) => (
                            <div className="rounded bg-slate-100 px-2 py-1 text-xs dark:bg-slate-800 dark:text-slate-300">{getValue(s)}</div>
                        ))}
                    </div>
                ) : (
                    placeholder
                )}

                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-4 w-4">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 15L12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9" />
                </svg>
            </button>
            <div className="relative w-full">
                {isOpen && (
                    <div className="scrollbar absolute z-10 mt-1 flex max-h-48 w-full flex-col overflow-y-scroll rounded border shadow-lg">
                        {Object.entries(options).map(([key, option], index) => (
                            <button onClick={() => toggleOption(key)} className={`flex cursor-pointer items-center justify-between gap-2 px-4 py-2 hover:bg-slate-200 ${index % 2 !== 0 ? "bg-slate-50 dark:bg-slate-800" : "bg-white dark:bg-slate-900"}`} key={key}>
                                {option}
                                {isSelected(key) && (
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="h-3 w-3">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                                    </svg>
                                )}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
